import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './BudgetSettingsModal.css';

function BudgetSettingsModal({ onClose, onSave }) {
  const [settings, setSettings] = useState({
    needs_percentage: 60,
    wants_percentage: 30,
    savings_percentage: 10
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings/budget');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Use defaults if fetch fails
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    const numValue = parseInt(value) || 0;
    setSettings(prev => ({
      ...prev,
      [field]: numValue
    }));
    setError(null);
  };

  const getTotal = () => {
    return settings.needs_percentage + settings.wants_percentage + settings.savings_percentage;
  };

  const handleSave = async () => {
    const total = getTotal();
    if (total !== 100) {
      setError(`Percentages must add up to 100% (currently: ${total}%)`);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      console.log('Saving settings:', settings);
      const response = await api.put('/settings/budget', settings);
      console.log('Save response:', response.data);
      
      // Show success message
      alert('✓ Settings saved successfully!');
      
      // Call parent handlers
      if (onSave) {
        onSave(response.data);
      }
      
      // Close modal
      onClose();
    } catch (error) {
      console.error('Error saving settings:', error);
      console.error('Error details:', error.response?.data);
      setError(`Failed to save settings: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({
      needs_percentage: 60,
      wants_percentage: 30,
      savings_percentage: 10
    });
    setError(null);
  };

  const total = getTotal();
  const isValid = total === 100;

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>⚙️ Budget Target Settings</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          <div className="settings-intro">
            <p>Customize your budget allocation targets. The AI will use these percentages when analyzing your finances.</p>
          </div>

          <div className="settings-group">
            <div className="setting-item needs">
              <div className="setting-header">
                <label>🏠 Needs (Essentials)</label>
                <span className="percentage-display">{settings.needs_percentage}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={settings.needs_percentage}
                onChange={(e) => handleChange('needs_percentage', e.target.value)}
                className="slider needs-slider"
              />
              <p className="setting-description">Housing, utilities, groceries, transportation, insurance</p>
            </div>

            <div className="setting-item wants">
              <div className="setting-header">
                <label>🎉 Wants (Lifestyle)</label>
                <span className="percentage-display">{settings.wants_percentage}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={settings.wants_percentage}
                onChange={(e) => handleChange('wants_percentage', e.target.value)}
                className="slider wants-slider"
              />
              <p className="setting-description">Entertainment, dining out, hobbies, subscriptions</p>
            </div>

            <div className="setting-item savings">
              <div className="setting-header">
                <label>💰 Savings & Investments</label>
                <span className="percentage-display">{settings.savings_percentage}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={settings.savings_percentage}
                onChange={(e) => handleChange('savings_percentage', e.target.value)}
                className="slider savings-slider"
              />
              <p className="setting-description">Emergency fund, retirement, investments, debt repayment</p>
            </div>
          </div>

          <div className={`total-indicator ${isValid ? 'valid' : 'invalid'}`}>
            <span className="total-label">Total:</span>
            <span className="total-value">{total}%</span>
            {isValid ? (
              <span className="valid-icon">✓</span>
            ) : (
              <span className="invalid-icon">Must equal 100%</span>
            )}
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="reset-btn" onClick={handleReset}>
            🔄 Reset to 60/30/10
          </button>
          <div className="action-buttons">
            <button className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button 
              className="save-btn" 
              onClick={handleSave}
              disabled={!isValid || saving}
            >
              {saving ? 'Saving...' : '✓ Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BudgetSettingsModal;
